import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  kbFreigabeDetailResponseSchema,
  kbFreigabeEntscheidResponseSchema,
  kbFreigabeListResponseSchema,
  type FreigabeEntscheidValue,
} from '@mindkeep/shared'
import { apiRequest } from '@/lib/api-client'

/**
 * Freigabe-Workflow (RNA M5). Der Retrieval-Filter aus M2 haelt alles zurueck,
 * was nicht `freigegeben` ist; diese Hooks sind der einzige Weg, das zu
 * aendern. Bewusst gibt es hier keine Sammelmutation: eine Freigabe ist eine
 * Personenhandlung an einem Dokument.
 */

export const kbFreigabeKeys = {
  all: ['kb-freigaben'] as const,
  list: (sort: 'asc' | 'desc') => [...kbFreigabeKeys.all, 'list', sort] as const,
  detail: (path: string) => [...kbFreigabeKeys.all, 'detail', path] as const,
}

export function useKbFreigaben(sort: 'asc' | 'desc' = 'desc') {
  return useQuery({
    queryKey: kbFreigabeKeys.list(sort),
    queryFn: () =>
      apiRequest({
        path: `/kb/freigaben?sort=${sort}`,
        schema: kbFreigabeListResponseSchema,
      }),
    staleTime: 15_000,
  })
}

export function useKbFreigabeDetail(path: string | null) {
  return useQuery({
    queryKey: kbFreigabeKeys.detail(path ?? ''),
    enabled: !!path,
    queryFn: () =>
      apiRequest({
        path: `/kb/freigaben/dokument?path=${encodeURIComponent(path as string)}`,
        schema: kbFreigabeDetailResponseSchema,
      }),
  })
}

export interface EntscheidInput {
  path: string
  entscheid: FreigabeEntscheidValue
  begruendung?: string
}

const ENTSCHEID_MELDUNG: Record<FreigabeEntscheidValue, string> = {
  freigegeben: 'Dokument freigegeben. Es kann jetzt in Antworten erscheinen.',
  zurueckgewiesen: 'Dokument zurueckgewiesen. Es bleibt aus Antworten ausgeschlossen.',
  ueberarbeitung: 'Zur Ueberarbeitung zurueckgegeben. Es bleibt aus Antworten ausgeschlossen.',
}

export function useKbFreigabeEntscheid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EntscheidInput) =>
      apiRequest({
        method: 'POST',
        path: '/kb/freigaben/entscheid',
        body,
        schema: kbFreigabeEntscheidResponseSchema,
      }),
    onSuccess: (data, variables) => {
      toast.success(ENTSCHEID_MELDUNG[variables.entscheid])
      // Der Statuswechsel wirkt erst, wenn die Chunks nachgezogen sind. Faellt
      // der Re-Ingest aus, bleibt das Dokument unsichtbar — das muss die
      // pruefende Person erfahren, statt es fuer erledigt zu halten.
      if (data.reindex.outcome === 'error') {
        toast.warning(
          `Der Chunk-Index wurde nicht nachgezogen: ${data.reindex.message} Das Dokument bleibt vorerst aus Antworten ausgeschlossen.`,
        )
      }
      void qc.invalidateQueries({ queryKey: kbFreigabeKeys.all })
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Entscheid konnte nicht gespeichert werden'),
  })
}
